import React, { createContext, useContext, useEffect, useState } from "react";
import { apiLogin, apiSignup, apiMe, apiLogout, apiGoogleSession } from "@/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshMe = async () => {
    const token = localStorage.getItem("ft_token");
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const me = await apiMe();
      setUser(me);
    } catch {
      localStorage.removeItem("ft_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // If returning from Google OAuth callback, AuthCallback handles it; skip /me here.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refreshMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password, role) => {
    try {
      const res = await apiLogin(email, password, role);
      localStorage.setItem("ft_token", res.token);
      setUser(res.user);
      return { ok: true };
    } catch (e) {
      return {
        ok: false,
        message: e?.response?.data?.detail || "Login failed. Please try again.",
      };
    }
  };

  const signup = async (data) => {
    try {
      const res = await apiSignup(data);
      return { ok: true, message: res.message, status: res.status };
    } catch (e) {
      return {
        ok: false,
        message: e?.response?.data?.detail || "Signup failed. Please try again.",
      };
    }
  };

  // Called by AuthCallback after Google redirect
  const completeGoogle = async (sessionId) => {
    try {
      const res = await apiGoogleSession(sessionId);
      localStorage.setItem("ft_token", res.token);
      setUser(res.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, message: e?.response?.data?.detail || "Google login failed" };
    }
  };

  // Directly set session (guest checkout / OTP flows)
  const setSession = (token, userData) => {
    localStorage.setItem("ft_token", token);
    setUser(userData);
  };

  const logout = async () => {
    try {
      await apiLogout();
    } catch {
      /* ignore */
    }
    localStorage.removeItem("ft_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, login, signup, logout, completeGoogle, refreshMe, setSession }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
